"use client"

import React, { useRef, useState } from "react"
import { useTranslations } from "next-intl"
import { SubmitHandler, useForm } from "react-hook-form"
import { z } from "zod"

import { zodResolver } from "@/lib/polyfill/zodResolver"
import { getRenderedRichText } from "@/components/ui/helpers/richtextRenderer"
import { cn } from "@/components/ui/helpers/utils"
import FriendlyCaptchaClient from "@/components/storyblok/common/FriendlyCaptchaClient"
import { RichtextStoryblok } from "@/components/storyblok/component-types"

interface UbNewsletterUnsubscribeFormClientProps {
  inxmaiUnsubscriptionServletUrl: string
  inxmailUnsubscription: string
  inxmailHttpRedirectUnsubscribe: string
  inxmailHttpRedirectErrorUnsubscribe: string
  inxmailCharset: string
  title_text?: string
  email_label?: string
  button_label?: string
  captchaHint?: RichtextStoryblok
}

const UbNewsletterUnsubscribeFormClient = (props: UbNewsletterUnsubscribeFormClientProps) => {
  const formRef = useRef<HTMLFormElement>(null)
  const t = useTranslations("storefront")
  const [sending, setSending] = useState(false)

  const validationSchema = z.object({
    INXMAIL_UNSUBSCRIPTION: z.string().min(1),
    INXMAIL_HTTP_REDIRECT: z.string().min(1),
    INXMAIL_HTTP_REDIRECT_ERROR: z.string().min(1),
    INXMAIL_CHARSET: z.string().min(1),
    email: z.email(),
  })

  type ValidationSchemaType = z.infer<typeof validationSchema>
  const { register, handleSubmit } = useForm<ValidationSchemaType>({ resolver: zodResolver(validationSchema) })

  const onSubmit: SubmitHandler<ValidationSchemaType> = () => {
    setSending(true)
    formRef.current?.submit()
  }

  return (
    <>
      <div className="size-full">
        <div
          className={cn("relative mx-auto flex h-auto flex-col justify-center gap-4 bg-transparent p-4 sm:p-2", {
            "pointer-events-none cursor-wait opacity-50": sending,
          })}
        >
          <div className="text-xl font-bold">{props.title_text ?? ""}</div>
          <form
            className="grid w-full grid-cols-1 gap-x-10 gap-y-5 xl:grid-cols-2"
            ref={formRef}
            action={props.inxmaiUnsubscriptionServletUrl}
            method="post"
            onSubmit={(e) => handleSubmit(onSubmit)(e)}
          >
            <input type="hidden" {...register("INXMAIL_UNSUBSCRIPTION")} value={props.inxmailUnsubscription} />
            <input type="hidden" {...register("INXMAIL_HTTP_REDIRECT")} value={props.inxmailHttpRedirectUnsubscribe} />
            <input
              type="hidden"
              {...register("INXMAIL_HTTP_REDIRECT_ERROR")}
              value={props.inxmailHttpRedirectErrorUnsubscribe}
            />
            <input type="hidden" {...register("INXMAIL_CHARSET")} value={props.inxmailCharset} />

            <div className="flex flex-col xl:col-span-2">
              <label className="text-lg font-bold" htmlFor="emailAddress">
                {props.email_label ?? ""}
              </label>
              <input
                className="h-10 focus:border-primary focus:ring-0"
                {...register("email")}
                type="email"
                required={true}
              />
            </div>

            <FriendlyCaptchaClient />
            <button
              className="w-full bg-primary px-[0.938rem] py-[0.563rem] text-lg text-white xl:col-span-2"
              type="submit"
              disabled={sending}
            >
              {props.button_label ?? ""}
            </button>
            {props.captchaHint?.content?.[0].content && (
              <p className="text-sm xl:col-span-2">{getRenderedRichText(props.captchaHint)}</p>
            )}
          </form>
        </div>
      </div>
    </>
  )
}

export default UbNewsletterUnsubscribeFormClient
